/**
 * Water2Skin quiz funnel.
 *
 * Ported from the Thats Quick export. Behaviour preserved:
 *  - screen state machine with history (quiz Back, browser back, refresh),
 *  - answer validation, progress bar, focus management,
 *  - answer / location education screens, analysis interstitial timings,
 *  - results rendering (breakdown cards, customer reports, hardness card),
 *  - analytics events on window + dataLayer,
 *  - Klaviyo subscribe on email submit, with quiz results as profile properties,
 *  - damage tabs (ARIA tabs with keyboard support),
 *  - mobile spacer that keeps the continue button reachable,
 *  - window.__quizTest / window.__W2S_QUIZ__ test hooks.
 *
 * Content comes from two JSON payloads rendered by Liquid:
 *  - [data-w2s-quiz-config]  (inside the funnel section)
 *  - [data-w2s-quiz-answers] (from the "Quiz funnel: answers" section)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'w2s-quiz-progress';
  var ANALYSIS_MESSAGE_INTERVAL = 220;
  var ANALYSIS_DURATION = 680;
  var HARDNESS_SCALE_MAX = 400;
  var MOBILE_QUERY = '(max-width: 760px)';
  var PREVIEW_SAMPLE = {
    location: 'london_se',
    concern: 'dry_hair',
    routine: 'targeted',
    skinFeel: 'tight',
    hairFeel: 'frizzy',
    shower: 'handheld',
    outcome: 'healthy_hair',
  };

  var instance = null;

  /**
   * Parses a JSON script element.
   * @param {Element|null} el
   * @returns {*}
   */
  function parseJson(el) {
    if (!el) {
      return null;
    }

    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Escapes text for safe insertion into innerHTML strings.
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Returns the inline shower icon SVG for an answer icon key.
   * Copied verbatim from the export.
   * @param {string} id
   * @returns {string}
   */
  function showerIcon(id) {
    var common = 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"';

    if (id === 'handheld') {
      return '<svg viewBox="0 0 48 34" ' + common + '><path d="M10 30c4-14 8-22 18-22"/><path d="M27 5c7 0 11 4 11 9H24c0-5 1-7 3-9Z"/><path d="M28 18v2m5-2v3m5-3v2"/></svg>';
    }

    if (id === 'rainfall') {
      return '<svg viewBox="0 0 48 34" ' + common + '><path d="M23 4h20v5"/><path d="M32 9h14v5H27c0-3 2-5 5-5Z"/><path d="M31 18v3m6-3v4m6-4v3"/></svg>';
    }

    if (id === 'both') {
      return '<svg viewBox="0 0 48 34" ' + common + '><path d="M4 29c2-10 5-15 11-16m12-9h17v5M12 10c5 0 8 3 8 7H9c0-4 1-6 3-7Zm21-1h12v4H29c0-2 2-4 4-4Z"/><path d="M12 20v2m5-2v2m17-4v3m6-3v3"/></svg>';
    }

    return '<svg viewBox="0 0 48 34" ' + common + '><circle cx="24" cy="17" r="13"/><path d="M20 13c0-3 2-5 5-5 3 0 5 2 5 5 0 4-6 4-6 8m0 5h.01"/></svg>';
  }

  var CHECK_ICON = '<span class="answer-check" aria-hidden="true"><svg viewBox="0 0 12 12"><path d="m2.2 6.1 2.2 2.2 5.4-5.1" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"></path></svg></span>';

  /**
   * Creates a quiz controller for one funnel root.
   * @param {HTMLElement} root - The `.w2s-quiz` element.
   */
  function Quiz(root) {
    this.root = root;
    this.config = parseJson(root.querySelector('[data-w2s-quiz-config]')) || {};
    var answerData = [];

    document.querySelectorAll('[data-w2s-quiz-answers]').forEach(function (node) {
      var answers = parseJson(node);

      if (Array.isArray(answers)) {
        answerData = answerData.concat(answers);
      }
    });

    this.answerData = answerData;
    this.strings = this.config.strings || {};
    this.questions = this.buildQuestions();

    this.answers = {};
    this.current = 'intro';
    this.navHistory = [];
    this.analysisTimer = null;
    this.analysisPulse = null;
    this.eventKeys = {};

    if (!this.answerData.length) {
      console.warn('[w2s-quiz] No answers found. Add the "Quiz funnel: answers" section.');
    }

    this.renderOptions();
    this.bind();
    this.initTabs();
    this.initConditionalScroll();

    if (!this.restore()) {
      this.show('intro', false, false);
    }

    this.initPreview();
  }

  /**
   * Merges question blocks with their answers.
   * @returns {Array}
   */
  Quiz.prototype.buildQuestions = function () {
    var answerData = this.answerData;

    return (this.config.questions || []).map(function (question) {
      var options = answerData.filter(function (answer) {
        return answer.question === question.key && answer.value;
      });

      return {
        id: question.key,
        title: question.title || '',
        step: question.step,
        followUp: question.followUp || 'education',
        visual: !!question.visual,
        image: question.image || null,
        options: options,
      };
    });
  };

  /**
   * @param {string} id
   * @returns {object|undefined}
   */
  Quiz.prototype.getQuestion = function (id) {
    return this.questions.filter(function (question) {
      return question.id === id;
    })[0];
  };

  /**
   * @param {string} questionId
   * @param {string} value
   * @returns {object|undefined}
   */
  Quiz.prototype.getAnswer = function (questionId, value) {
    var question = this.getQuestion(questionId);

    if (!question) {
      return undefined;
    }

    return question.options.filter(function (option) {
      return option.value === value;
    })[0];
  };

  /**
   * @param {string} questionId
   * @param {string} value
   * @returns {string}
   */
  Quiz.prototype.answerLabel = function (questionId, value) {
    var answer = this.getAnswer(questionId, value);

    return answer ? answer.label : '';
  };

  /**
   * Ordered question keys.
   * @returns {Array<string>}
   */
  Quiz.prototype.sequence = function () {
    return this.questions.map(function (question) {
      return question.id;
    });
  };

  /**
   * @param {string} id
   * @returns {string}
   */
  Quiz.prototype.nextFor = function (id) {
    var seq = this.sequence();
    var index = seq.indexOf(id);

    return index < seq.length - 1 ? seq[index + 1] : 'analysis';
  };

  /**
   * Renders answer options for every question screen.
   */
  Quiz.prototype.renderOptions = function () {
    var self = this;

    this.questions.forEach(function (question) {
      var screen = self.root.querySelector('[data-screen="' + question.id + '"]');
      var mount = screen && screen.querySelector('[data-options]');

      if (!mount) {
        return;
      }

      mount.innerHTML = question.options.map(function (option) {
        var checked = self.answers[question.id] === option.value;
        var icon = '';

        if (question.visual) {
          icon = option.iconImage
            ? '<img alt="" src="' + escapeHtml(option.iconImage) + '">'
            : showerIcon(option.icon);
        }

        var body = question.visual
          ? icon + '<span class="shower-name">' + escapeHtml(option.label) + '</span>'
          : '<span>' + escapeHtml(option.label) + '</span>';

        return '<button type="button" class="choice" data-testid="option" data-question="' + escapeHtml(question.id) + '" data-value="' + escapeHtml(option.value) + '" aria-pressed="' + (checked ? 'true' : 'false') + '">' +
          '<span class="choice-body">' + body + CHECK_ICON + '</span>' +
          '</button>';
      }).join('');
    });
  };

  /**
   * Fires a quiz analytics event once per key.
   * @param {string} name
   * @param {object} [detail]
   */
  Quiz.prototype.emit = function (name, detail) {
    var key = name + ':' + ((detail && detail.question) || (detail && detail.recommendation) || this.current);

    if (this.eventKeys[key]) {
      return;
    }

    this.eventKeys[key] = true;

    var payload = { event: name, quiz: 'water2skin', detail: detail || {} };

    try {
      window.dispatchEvent(new CustomEvent(name, { detail: payload }));

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(payload);
      }
    } catch (error) {
      // Analytics must never break the quiz.
    }
  };

  /**
   * @param {string} id - Screen id.
   */
  Quiz.prototype.updateProgress = function (id) {
    var educationQuestion = id.indexOf('education-') === 0 ? id.replace('education-', '') : null;
    var question = this.getQuestion(educationQuestion || id);
    var wrap = this.root.querySelector('#w2s-quiz-progress');
    var total = this.questions.length;
    var hardness = this.hardnessQuestion();
    var step = id === 'locationEducation' ? hardness && hardness.step : question && question.step;

    if (!wrap) {
      return;
    }

    if (!step) {
      wrap.classList.remove('show');
      wrap.setAttribute('aria-hidden', 'true');
      return;
    }

    var track = wrap.querySelector('.track');
    var text = wrap.querySelector('#progressText');
    var fill = wrap.querySelector('#progressFill');
    var template = this.strings.progressStep || 'Question {step} / {total}';

    wrap.classList.add('show');
    wrap.setAttribute('aria-hidden', 'false');
    track.setAttribute('aria-valuemax', String(total));
    track.setAttribute('aria-valuenow', String(step));
    text.textContent = template.replace('{step}', step).replace('{total}', total);
    fill.style.width = (step / total * 100) + '%';
  };

  /**
   * Shows a screen.
   * @param {string} id
   * @param {boolean} record - Push the current screen to history.
   * @param {boolean} pushBrowser - Push a browser history entry.
   */
  Quiz.prototype.show = function (id, record, pushBrowser) {
    var self = this;
    var el = id === 'results'
      ? this.root.querySelector('#results')
      : this.root.querySelector('[data-screen="' + id + '"]');

    if (!el) {
      return;
    }

    var leaving = !this.applyingHistory && pushBrowser ? this.snapshot() : null;

    if (record && this.current && this.current !== id) {
      this.navHistory.push(this.current);
    }

    this.root.querySelectorAll('[data-screen]').forEach(function (screen) {
      screen.classList.remove('active');
      screen.style.display = '';
    });

    if (id === 'locationEducation') {
      this.renderLocationEducation();
    }

    if (id.indexOf('education-') === 0) {
      this.renderAnswerEducation(id);
    }

    el.classList.add('active');
    el.style.display = id === 'results' ? 'block' : 'flex';
    this.current = id;
    this.updateProgress(id);
    window.scrollTo(0, 0);

    var heading = el.querySelector('h1,h2');

    if (heading && !this.applyingHistory) {
      setTimeout(function () {
        heading.focus({ preventScroll: true });
      }, 30);
    }

    if (!this.applyingHistory) {
      if (leaving && window.history && window.history.replaceState) {
        window.history.replaceState(
          { w2sQuiz: leaving },
          '',
          window.location.href
        );
      }

      this.remember(!!pushBrowser);
    }

    if (!this.applyingHistory && this.getQuestion(id)) {
      this.emit('quiz_question_view', { question: id, step: this.getQuestion(id).step });
    }

    self.scheduleConditionalScroll();
  };

  /**
   * @param {object} question
   * @returns {string|null}
   */
  Quiz.prototype.selectedFromDOM = function (question) {
    var node = this.root.querySelector(
      '.choice[data-question="' + question.id + '"][aria-pressed="true"]'
    );

    return node ? node.getAttribute('data-value') : null;
  };

  /**
   * Validates and advances from a question screen.
   * @param {string} id
   */
  Quiz.prototype.next = function (id) {
    var question = this.getQuestion(id);

    if (!question) {
      return;
    }

    var value = this.selectedFromDOM(question);
    var error = this.root.querySelector('#error-' + id);

    if (!value) {
      if (error) {
        error.textContent = this.strings.error || 'Choose an answer to continue.';
      }
      return;
    }

    if (error) {
      error.textContent = '';
    }

    this.answers[id] = value;
    this.emit('quiz_answer', { question: id, step: question.step });

    if (question.followUp === 'hardness') {
      this.hardnessKey = id;
      this.show('locationEducation', true, true);
      return;
    }

    if (question.followUp === 'none') {
      var lastQuestion = this.sequence()[this.sequence().length - 1];

      this.show(id === lastQuestion ? 'emailCapture' : this.nextFor(id), true, true);
      return;
    }

    this.show('education-' + id, true, true);
  };

  Quiz.prototype.goBack = function () {
    this.clearAnalysis();

    var state = window.history && window.history.state;

    if (state && state.w2sQuiz && this.navHistory.length && window.history.back) {
      window.history.back();
      return;
    }

    var prev = this.navHistory.pop();

    this.show(prev || 'intro', false, false);
  };

  /**
   * @param {string} id
   * @returns {boolean}
   */
  Quiz.prototype.screenExists = function (id) {
    if (!id) {
      return false;
    }

    if (id === 'results') {
      return !!this.root.querySelector('#results');
    }

    return !!this.root.querySelector('[data-screen="' + id + '"]');
  };

  /**
   * @param {object} answers
   * @returns {Object<string, string>}
   */
  Quiz.prototype.validAnswers = function (answers) {
    var self = this;
    var next = {};

    Object.keys(answers || {}).forEach(function (id) {
      var value = answers[id];

      if (self.getAnswer(id, value)) {
        next[id] = value;
      }
    });

    return next;
  };

  /**
   * @returns {object}
   */
  Quiz.prototype.snapshot = function () {
    return {
      screen: this.current,
      answers: JSON.parse(JSON.stringify(this.answers)),
      navHistory: this.navHistory.filter(function (id) {
        return !!id;
      }),
      hardnessKey: this.hardnessKey || '',
    };
  };

  /**
   * Saves the current step in this tab.
   */
  Quiz.prototype.persist = function () {
    try {
      sessionStorage.setItem(
        STORAGE_KEY + ':' + window.location.pathname,
        JSON.stringify(this.snapshot())
      );
    } catch (error) {
      // Private browsing can block storage. The in-page history still works.
    }
  };

  /**
   * @param {boolean} push - Add a browser history entry. Otherwise update the current one.
   */
  Quiz.prototype.remember = function (push) {
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    this.persist();

    if (!window.history) {
      return;
    }

    var state = { w2sQuiz: this.snapshot() };
    var url = window.location.href;

    if (push && window.history.pushState) {
      window.history.pushState(state, '', url);
      return;
    }

    if (window.history.replaceState) {
      window.history.replaceState(state, '', url);
    }
  };

  /**
   * @param {object|null} saved
   * @returns {boolean}
   */
  Quiz.prototype.readSaved = function (saved) {
    if (!saved || !saved.screen) {
      return false;
    }

    var screen = this.screenExists(saved.screen) ? saved.screen : '';

    if (!screen || screen === 'intro') {
      return false;
    }

    this.answers = this.validAnswers(saved.answers);
    this.navHistory = (saved.navHistory || []).filter(function (id) {
      return this.screenExists(id);
    }, this);
    this.hardnessKey = saved.hardnessKey || '';
    this.renderOptions();
    this.applyingHistory = true;

    if (screen === 'results') {
      this.buildResults();
    }

    this.show(screen, false, false);
    this.applyingHistory = false;

    if (screen === 'analysis') {
      this.startAnalysis();
    }

    this.persist();

    return true;
  };

  /**
   * Restores the step for this history entry, or the last step saved in this tab.
   * @returns {boolean}
   */
  Quiz.prototype.restore = function () {
    if (window.Shopify && window.Shopify.designMode) {
      return false;
    }

    var fromHistory = window.history
      && window.history.state
      && window.history.state.w2sQuiz;

    if (this.readSaved(fromHistory)) {
      return true;
    }

    var stored = null;

    try {
      stored = JSON.parse(
        sessionStorage.getItem(STORAGE_KEY + ':' + window.location.pathname) || 'null'
      );
    } catch (error) {
      stored = null;
    }

    if (!this.readSaved(stored)) {
      return false;
    }

    this.remember(false);

    return true;
  };

  /**
   * Applies a browser-history snapshot, including the intro.
   * @param {object} saved
   */
  Quiz.prototype.applySnapshot = function (saved) {
    this.clearAnalysis();

    if (!saved) {
      return;
    }

    if (this.readSaved(saved)) {
      return;
    }

    this.answers = this.validAnswers(saved.answers);
    this.navHistory = [];
    this.hardnessKey = '';
    this.renderOptions();
    this.applyingHistory = true;
    this.show('intro', false, false);
    this.applyingHistory = false;
    this.persist();
  };

  /**
   * @param {HTMLElement} el
   */
  Quiz.prototype.handleChoice = function (el) {
    var questionId = el.getAttribute('data-question');
    var value = el.getAttribute('data-value');
    var question = this.getQuestion(questionId);

    if (!question || !value) {
      return;
    }

    this.answers[question.id] = value;
    this.root.querySelectorAll(
      '.choice[data-question="' + question.id + '"]'
    ).forEach(function (choice) {
      choice.setAttribute(
        'aria-pressed',
        choice.getAttribute('data-value') === value ? 'true' : 'false'
      );
    });
    this.remember(false);

    var error = this.root.querySelector('#error-' + question.id);

    if (error) {
      error.textContent = '';
    }
  };

  /**
   * Segmentation: which concern group the answers point to.
   * @returns {string}
   */
  Quiz.prototype.concernGroup = function () {
    var self = this;
    var group = '';

    this.questions.some(function (question) {
      var answer = self.getAnswer(question.id, self.answers[question.id]);

      if (answer && answer.segment) {
        group = answer.segment;
        return true;
      }

      return false;
    });

    return group || 'overall';
  };

  /**
   * First product block whose question and answer match, otherwise the
   * block left blank as the fallback.
   * @returns {object|null}
   */
  Quiz.prototype.matchedOffer = function () {
    var offers = this.config.offers || [];
    var self = this;
    var fallback = null;
    var index;

    for (index = 0; index < offers.length; index += 1) {
      var offer = offers[index];
      var question = (offer.question || '').trim();
      var raw = (offer.value || '').trim();

      if (!question || !raw) {
        if (!fallback) {
          fallback = offer;
        }

        continue;
      }

      var values = raw.split(',').map(function (value) {
        return value.trim();
      });

      if (values.indexOf(self.answers[question]) !== -1) {
        return offer;
      }
    }

    return fallback;
  };

  /**
   * @returns {{group: string, productKey: string}}
   */
  Quiz.prototype.resolve = function () {
    var offer = this.matchedOffer();

    return {
      group: this.concernGroup(),
      productKey: (offer && offer.handle) || 'default',
    };
  };

  /**
   * @returns {Array<{impact: string, title: string, answer: string, copy: string}>}
   */
  Quiz.prototype.getBreakdownCards = function () {
    var self = this;

    function label(id, fallback) {
      return self.answerLabel(id, self.answers[id]) || fallback;
    }

    return (this.config.breakdownCards || []).map(function (card) {
      var answer = self.getAnswer(card.question, self.answers[card.question]);
      var text = '<strong>' + escapeHtml(card.answerPrefix) + '</strong> ' + escapeHtml(label(card.question, card.fallback)) + '.';

      if (card.secondaryQuestion && card.secondaryQuestion !== 'none') {
        text += ' ' + escapeHtml(label(card.secondaryQuestion, card.secondaryFallback)) + '.';
      }

      return {
        impact: card.impact,
        title: card.title,
        answer: text,
        copy: (answer && answer.breakdownCopy) || card.copyFallback,
      };
    });
  };

  Quiz.prototype.renderBreakdown = function () {
    var grid = this.root.querySelector('#breakdownGrid');

    if (!grid) {
      return;
    }

    grid.innerHTML = this.getBreakdownCards().map(function (card) {
      return '<article class="breakdown-card"><p class="breakdown-impact">' + escapeHtml(card.impact) + '</p><h3>' + escapeHtml(card.title) + '</h3><p class="guide-answer">' + card.answer + '</p><p class="guide-explanation">' + escapeHtml(card.copy) + '</p></article>';
    }).join('');
  };

  /**
   * @returns {Array<{value: string, title: string, copy: string}>}
   */
  Quiz.prototype.getCustomerReports = function () {
    var self = this;

    return (this.config.reports || []).map(function (report) {
      var answer = self.answerLabel(report.question, self.answers[report.question]) || report.fallback;

      if (report.lowercase) {
        answer = answer.toLowerCase();
      }

      return {
        value: report.value,
        title: report.title,
        copy: (report.copy || '').replace('{answer}', answer),
      };
    });
  };

  Quiz.prototype.renderCustomerReports = function () {
    var list = this.root.querySelector('#reportsList');

    if (!list) {
      return;
    }

    list.innerHTML = this.getCustomerReports().map(function (stat) {
      return '<article class="report-row"><p class="report-number">' + escapeHtml(stat.value) + '</p><div class="report-copy"><h3>' + escapeHtml(stat.title) + '</h3><p>' + escapeHtml(stat.copy) + '</p></div></article>';
    }).join('');
  };

  /**
   * @param {number} value - mg/L CaCO3.
   * @returns {string}
   */
  Quiz.prototype.hardnessRating = function (value) {
    var ratings = (this.strings.hardness && this.strings.hardness.ratings) || {};

    if (value <= 100) {
      return ratings.soft || 'Soft';
    }

    if (value <= 150) {
      return ratings.slightly || 'Slightly hard';
    }

    if (value <= 200) {
      return ratings.moderate || 'Moderately hard';
    }

    if (value <= 300) {
      return ratings.hard || 'Hard';
    }

    return ratings.very || 'Very hard';
  };

  /**
   * @param {number} value - mg/L CaCO3.
   * @returns {string}
   */
  Quiz.prototype.hardnessExplanation = function (value) {
    var texts = (this.strings.hardness && this.strings.hardness.explanations) || {};

    if (value <= 100) {
      return texts.soft || '';
    }

    if (value <= 200) {
      return texts.mid || '';
    }

    return texts.hard || '';
  };

  /**
   * The question that opens the water-hardness screen.
   * @returns {object|undefined}
   */
  Quiz.prototype.hardnessQuestion = function () {
    var key = this.hardnessKey;
    var match = key && this.getQuestion(key);

    if (match) {
      return match;
    }

    return this.questions.filter(function (question) {
      return question.followUp === 'hardness';
    })[0];
  };

  Quiz.prototype.selectedRegion = function () {
    var question = this.hardnessQuestion();

    if (!question) {
      return undefined;
    }

    return this.getAnswer(question.id, this.answers[question.id]) || question.options[0];
  };

  /**
   * Fills a hardness card (water info screen or results card).
   * @param {Element} container
   */
  Quiz.prototype.renderHardness = function (container) {
    var region = this.selectedRegion();

    if (!container || !region) {
      return;
    }

    var name = region.regionName || region.label;
    var value = Number(region.hardness) || 0;
    var rating = this.hardnessRating(value);
    var hardness = this.strings.hardness || {};
    var waterWord = hardness.waterWord || 'water';
    var meaning = hardness.meaningLabel || 'What this means';
    var position = Math.min(97, Math.max(3, (value / HARDNESS_SCALE_MAX) * 100));

    function set(selector, text) {
      var el = container.querySelector(selector);

      if (el) {
        el.textContent = text;
      }
    }

    set('.location-area', name);
    set('.location-rating', rating.toLowerCase());
    set('.result-name', name.charAt(0).toUpperCase() + name.slice(1));
    set('.result-rating', rating.toLowerCase() + ' ' + waterWord);
    set('.result-number', String(value));

    var explainer = container.querySelector('.hardness-explainer');

    if (explainer) {
      explainer.innerHTML = '<strong>' + escapeHtml(meaning) + '</strong> ' + escapeHtml(this.hardnessExplanation(value));
    }

    var marker = container.querySelector('.hardness-marker');

    if (marker) {
      marker.style.left = position + '%';
    }

    var gauge = container.querySelector('.hardness-gauge');

    if (gauge) {
      gauge.setAttribute(
        'aria-label',
        name + ': ' + value + ' milligrams per litre calcium carbonate, ' + rating + '. Water hardness scale from soft to very hard.'
      );
    }
  };

  Quiz.prototype.renderLocationEducation = function () {
    this.renderHardness(this.root.querySelector('#locationEducation'));
  };

  Quiz.prototype.renderResultConsultation = function () {
    this.renderHardness(this.root.querySelector('#resultConsultationCard'));
  };

  /**
   * Writes a title with an optional highlighted span.
   * @param {Element|null} el
   * @param {string} title
   * @param {string} highlight
   */
  function setHighlightedTitle(el, title, highlight) {
    if (!el) {
      return;
    }

    el.textContent = '';

    var start = highlight ? title.indexOf(highlight) : -1;

    if (start < 0) {
      el.textContent = title;
      return;
    }

    var before = title.slice(0, start);
    var after = title.slice(start + highlight.length);
    var markText = highlight;
    var trailing = after.match(/^\s*([.!?…]+)\s*$/);

    if (trailing) {
      markText += trailing[1];
      after = '';
    }

    var mark = document.createElement('span');

    mark.className = 'education-title-highlight';
    mark.textContent = markText;
    el.append(document.createTextNode(before));
    el.append(mark, document.createTextNode(after));
  }

  /**
   * @param {string} id - e.g. 'education-concern'.
   */
  Quiz.prototype.renderAnswerEducation = function (id) {
    var questionId = id.replace('education-', '');
    var question = this.getQuestion(questionId);
    var answer = this.getAnswer(questionId, this.answers[questionId]);
    var screen = this.root.querySelector('[data-screen="' + id + '"]');

    if (!screen || !answer || !answer.education) {
      return;
    }

    var content = answer.education;

    if (!content.title && !content.heading && !content.body) {
      return;
    }
    var img = screen.querySelector('.location-visual img');

    if (img && question && question.image) {
      img.src = question.image;
    }

    setHighlightedTitle(screen.querySelector('.education-visual-title'), content.title || '', content.highlight || '');

    var lead = screen.querySelector('.education-lead');
    var heading = screen.querySelector('.education-heading');
    var body = screen.querySelector('.education-body');

    if (lead) {
      lead.textContent = content.lead || '';
    }

    if (heading) {
      heading.textContent = content.heading || '';
    }

    if (body) {
      body.textContent = content.body || '';
    }
  };

  Quiz.prototype.clearAnalysis = function () {
    if (this.analysisTimer) {
      clearTimeout(this.analysisTimer);
      this.analysisTimer = null;
    }

    if (this.analysisPulse) {
      clearInterval(this.analysisPulse);
      this.analysisPulse = null;
    }
  };

  Quiz.prototype.startAnalysis = function () {
    var self = this;
    var messages = (this.strings.analysisMessages || []).filter(Boolean);
    var el = this.root.querySelector('#analysisMessage');
    var index = 0;

    this.clearAnalysis();

    if (el && messages[0]) {
      el.textContent = messages[0];
    }

    this.analysisPulse = setInterval(function () {
      index += 1;

      if (el && messages[index]) {
        el.textContent = messages[index];
      }
    }, ANALYSIS_MESSAGE_INTERVAL);

    this.analysisTimer = setTimeout(function () {
      self.clearAnalysis();
      self.buildResults();
      self.show('results', true, true);
    }, ANALYSIS_DURATION);
  };

  /**
   * Writes a matched product over both offer panels. Blank fields keep the
   * copy already rendered from the section settings.
   * @param {object|null} offer
   */
  Quiz.prototype.renderOffer = function (offer) {
    if (!offer) {
      return;
    }

    this.root.querySelectorAll('.offer-block').forEach(function (block) {
      function setText(selector, value) {
        if (!value) {
          return;
        }

        var el = block.querySelector(selector);

        if (el) {
          el.textContent = value;
        }
      }

      setText('.offer-heading', offer.heading);
      setText('.offer-title', offer.title);
      setText('.offer-body', offer.body);
      setText('.offer-price', offer.price);
      setText('.offer-compare-price', offer.comparePrice);
      setText('.offer-badge', offer.badge);
      setText('.offer-cta', offer.ctaLabel);

      var image = block.querySelector('.offer-image');

      if (image && offer.image) {
        image.src = offer.image;

        if (offer.imageAlt) {
          image.alt = offer.imageAlt;
        }
      }

      var cta = block.querySelector('.offer-cta');

      if (cta && offer.ctaUrl) {
        cta.setAttribute('href', offer.ctaUrl);
      }
    });
  };

  /**
   * @returns {{group: string, productKey: string}}
   */
  Quiz.prototype.buildResults = function () {
    var result = this.resolve();

    this.renderBreakdown();
    this.renderCustomerReports();
    this.renderResultConsultation();
    this.renderOffer(this.matchedOffer());

    if (!this.applyingHistory) {
      this.emit('quiz_complete', { recommendation: result.productKey, concern: result.group });
    }

    return result;
  };

  Quiz.prototype.reset = function () {
    this.clearAnalysis();
    this.answers = {};
    this.navHistory = [];
    this.eventKeys = {};

    var email = this.root.querySelector('#quizEmail');

    if (email) {
      email.value = '';
    }

    this.setEmailPending(false);
    this.setEmailStatus('', false);

    try {
      sessionStorage.removeItem(STORAGE_KEY + ':' + window.location.pathname);
    } catch (error) {
      // Storage can be unavailable. The reset still returns to the intro.
    }

    this.renderOptions();
    this.show('intro', false, false);
  };

  /**
   * Replaces answers (test hook / editor preview) and re-renders options.
   * @param {object} answers
   * @returns {{group: string, productKey: string}}
   */
  Quiz.prototype.setAnswers = function (answers) {
    this.answers = JSON.parse(JSON.stringify(answers || {}));
    this.renderOptions();

    return this.resolve();
  };

  /**
   * @returns {object}
   */
  Quiz.prototype.getState = function () {
    return {
      screen: this.current,
      navHistory: this.navHistory.slice(),
      sequence: this.sequence(),
      answers: JSON.parse(JSON.stringify(this.answers)),
    };
  };

  /**
   * Shows or clears the message under the email form.
   * @param {string} message
   * @param {boolean} isError
   */
  Quiz.prototype.setEmailStatus = function (message, isError) {
    var status = this.root.querySelector('#emailCaptureStatus');

    if (!status) {
      return;
    }

    if (!message) {
      status.hidden = true;
      status.textContent = '';
      return;
    }

    status.hidden = false;
    status.textContent = message;
    status.setAttribute('role', isError ? 'alert' : 'status');
  };

  /**
   * @param {boolean} pending
   */
  Quiz.prototype.setEmailPending = function (pending) {
    var form = this.root.querySelector('#emailCaptureForm');

    if (!form) {
      return;
    }

    form.querySelectorAll('button').forEach(function (button) {
      button.disabled = pending;
    });
  };

  /**
   * Public company ID from the Klaviyo onsite script already on the page.
   * @returns {string}
   */
  Quiz.prototype.klaviyoCompanyId = function () {
    var scripts = document.querySelectorAll('script[src]');
    var index;

    for (index = 0; index < scripts.length; index += 1) {
      var src = scripts[index].getAttribute('src') || '';
      var queryMatch = src.match(/[?&]company_id=([A-Za-z0-9]+)/);

      if (queryMatch) {
        return queryMatch[1];
      }

      var pathMatch = src.match(
        /static\.klaviyo\.com\/onsite\/js\/([A-Za-z0-9]+)\//
      );

      if (pathMatch) {
        return pathMatch[1];
      }
    }

    return '';
  };

  /**
   * Flat profile properties for the latest quiz attempt.
   * @returns {Object<string, string>}
   */
  Quiz.prototype.klaviyoProperties = function () {
    var self = this;
    var offer = this.matchedOffer();
    var result = this.resolve();
    var titleEl = this.root.querySelector('.offer-title');
    var properties = {
      quiz_result_group: result.group,
      quiz_product: result.productKey,
      quiz_product_title: (offer && offer.title)
        || (titleEl ? titleEl.textContent.trim() : ''),
    };
    var parts = [];

    this.questions.forEach(function (question) {
      var value = self.answers[question.id];

      if (!value) {
        return;
      }

      var label = self.answerLabel(question.id, value) || value;
      var key = 'quiz_' + String(question.id).replace(/[^A-Za-z0-9_]/g, '_');

      properties[key] = label;

      if (question.title) {
        parts.push(question.title + ': ' + label);
      }
    });

    properties.quiz_answers = parts.join(' | ');

    var hardnessQuestion = this.hardnessQuestion();
    var region = hardnessQuestion
      && this.answers[hardnessQuestion.id]
      && this.getAnswer(hardnessQuestion.id, this.answers[hardnessQuestion.id]);

    if (region) {
      var mg = Number(region.hardness) || 0;

      properties.quiz_area = region.regionName || region.label || '';
      properties.quiz_hardness = String(mg);
      properties.quiz_hardness_rating = this.hardnessRating(mg);
    }

    return properties;
  };

  /**
   * Subscribes the address and stores the quiz results on that profile.
   * @param {string} email
   * @returns {Promise<void>}
   */
  Quiz.prototype.subscribeQuizResults = function (email) {
    var klaviyo = this.config.klaviyo || {};
    var companyId = this.klaviyoCompanyId();
    var properties = this.klaviyoProperties();
    var payload = { email: email };

    if (!companyId || !klaviyo.listId) {
      return Promise.reject(new Error('Klaviyo is not configured'));
    }

    Object.keys(properties).forEach(function (key) {
      payload[key] = properties[key];
    });

    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      window.klaviyo.identify(payload);
    } else {
      window.klaviyo = window.klaviyo || [];

      if (typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', payload]);
      }
    }

    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id='
        + encodeURIComponent(companyId),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: '2026-07-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: klaviyo.formId || 'Quiz results',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: properties,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED',
                        },
                      },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: klaviyo.listId,
                },
              },
            },
          },
        }),
      }
    ).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (errorBody) {
          console.error(
            'Klaviyo subscribe failed',
            response.status,
            errorBody
          );
          throw new Error('Klaviyo subscribe failed');
        });
      }

      window.dispatchEvent(new CustomEvent('klaviyoForms', {
        detail: {
          type: 'submit',
          formId: klaviyo.formId || '',
          formName: 'Quiz results',
          companyId: companyId,
          metaData: {
            email: email,
          },
        },
      }));
    });
  };

  Quiz.prototype.bind = function () {
    var self = this;
    var form = this.root.querySelector('#emailCaptureForm');

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        if (!form.reportValidity()) {
          return;
        }

        var emailInput = form.querySelector('#quizEmail');
        var email = emailInput ? emailInput.value.trim() : '';
        var listId = self.config.klaviyo && self.config.klaviyo.listId;

        self.emit('quiz_email_capture_submit', { screen: 'emailCapture' });
        self.setEmailStatus('', false);

        if (!listId) {
          self.show('analysis', true, true);
          self.startAnalysis();
          return;
        }

        self.setEmailPending(true);

        self.subscribeQuizResults(email).then(function () {
          self.setEmailPending(false);
          self.show('analysis', true, true);
          self.startAnalysis();
        }).catch(function () {
          self.setEmailPending(false);
          self.setEmailStatus(
            self.strings.emailError
              || 'Something went wrong. Please try again.',
            true
          );
        });
      });
    }

    this.root.addEventListener('click', function (event) {
      var choice = event.target.closest && event.target.closest('#questionMount .choice');

      if (choice) {
        self.handleChoice(choice);
      }

      var cta = event.target.closest && event.target.closest('.cta');

      if (cta) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'water2skin_bridge_cta_click',
          placement: cta.getAttribute('data-placement') || 'unknown',
        });
      }

      var trigger = event.target.closest && event.target.closest('[data-action]');

      if (!trigger) {
        return;
      }

      var action = trigger.getAttribute('data-action');

      if (action === 'start') {
        var first = self.sequence()[0] || 'emailCapture';

        self.show(first, true, true);
      }

      if (action === 'next') {
        self.next(trigger.getAttribute('data-question'));
      }

      if (action === 'education-next') {
        if (self.current === 'locationEducation') {
          var hardness = self.hardnessQuestion();

          self.show(self.nextFor(hardness ? hardness.id : ''), true, true);
        } else {
          var source = self.current.replace('education-', '');
          var last = self.sequence()[self.sequence().length - 1];

          self.show(source === last ? 'emailCapture' : self.nextFor(source), true, true);
        }
      }

      if (action === 'back') {
        self.goBack();
      }

      if (action === 'skip-email') {
        self.emit('quiz_email_capture_skip', { screen: 'emailCapture' });
        self.show('analysis', true, true);
        self.startAnalysis();
      }

      if (action === 'retake') {
        self.reset();
      }
    });
  };

  /**
   * Damage variant tabs (ARIA tablist with arrow-key navigation).
   */
  Quiz.prototype.initTabs = function () {
    var section = this.root.querySelector('[data-damage-variant="3"]');

    if (!section) {
      return;
    }

    var tabs = Array.from(section.querySelectorAll('[data-damage-tab]'));

    function selectTab(id, focus) {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-damage-tab') === id;

        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;

        if (on && focus) {
          tab.focus();
        }
      });

      section.querySelectorAll('[data-damage-panel]').forEach(function (panel) {
        var on = panel.getAttribute('data-damage-panel') === id;

        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        selectTab(tab.getAttribute('data-damage-tab'), false);
      });

      tab.addEventListener('keydown', function (event) {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) === -1) {
          return;
        }

        event.preventDefault();

        var next = index;

        if (event.key === 'ArrowLeft') {
          next = (index - 1 + tabs.length) % tabs.length;
        }

        if (event.key === 'ArrowRight') {
          next = (index + 1) % tabs.length;
        }

        if (event.key === 'Home') {
          next = 0;
        }

        if (event.key === 'End') {
          next = tabs.length - 1;
        }

        selectTab(tabs[next].getAttribute('data-damage-tab'), true);
      });
    });

    this.selectTab = selectTab;

    if (tabs[0]) {
      selectTab(tabs[0].getAttribute('data-damage-tab'), false);
    }
  };

  /**
   * On small screens, inserts a spacer above the actions so the primary
   * button sits just below the fold of the last content block.
   */
  Quiz.prototype.initConditionalScroll = function () {
    var self = this;
    var main = this.root.querySelector('main');

    this.scrollTimer = 0;

    this.syncConditionalScroll = function () {
      var active = self.root.querySelector('.question-screen.active, .location-education.active');

      self.root.querySelectorAll('.mobile-conditional-scroll-spacer').forEach(function (node) {
        if (!active || !active.contains(node)) {
          node.remove();
        }
      });

      if (!active || !matchMedia(MOBILE_QUERY).matches) {
        return;
      }

      var actions = active.querySelector('.actions, .location-actions');

      if (!actions) {
        return;
      }

      var button = actions.querySelector('.btn-primary, .location-continue, button:not(.back)');
      var parent = actions.parentElement;

      if (!button || !parent) {
        return;
      }

      var spacer = parent.querySelector(':scope > .mobile-conditional-scroll-spacer');

      if (!spacer) {
        spacer = document.createElement('div');
        spacer.className = 'mobile-conditional-scroll-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        parent.insertBefore(spacer, actions);
      }

      spacer.style.height = '0px';

      var siblings = Array.from(parent.children);
      var spacerIndex = siblings.indexOf(spacer);
      var candidates = siblings.slice(0, spacerIndex).filter(function (node) {
        var rect = node.getBoundingClientRect();

        return getComputedStyle(node).display !== 'none' && rect.height > 0;
      });
      var last = candidates[candidates.length - 1];

      if (!last || !active.classList.contains('active')) {
        return;
      }

      var lastBottom = last.getBoundingClientRect().bottom;
      var buttonTop = button.getBoundingClientRect().top;
      var remainingScroll = Math.max(0, document.documentElement.scrollHeight - innerHeight - scrollY);
      var required = Math.max(0, Math.ceil(lastBottom + 34 - buttonTop - remainingScroll));

      spacer.style.height = required + 'px';
    };

    if (main) {
      this.observer = new MutationObserver(function (mutations) {
        var relevant = mutations.some(function (mutation) {
          if (mutation.type === 'attributes') {
            return true;
          }

          return Array.from(mutation.addedNodes).some(function (node) {
            return !(node.nodeType === 1 && node.classList.contains('mobile-conditional-scroll-spacer'));
          });
        });

        if (relevant) {
          self.scheduleConditionalScroll();
        }
      });

      this.observer.observe(main, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        self.scheduleConditionalScroll();
      });
    }

    this.scheduleConditionalScroll();
  };

  Quiz.prototype.scheduleConditionalScroll = function () {
    var self = this;

    if (!this.syncConditionalScroll) {
      return;
    }

    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(function () {
      self.syncConditionalScroll();
    }, 0);
  };

  /**
   * Theme editor: jump to the configured screen with sample answers.
   */
  Quiz.prototype.initPreview = function () {
    var screen = this.root.getAttribute('data-preview-screen');
    var designMode = window.Shopify && window.Shopify.designMode;

    if (!designMode || !screen || screen === 'intro') {
      return;
    }

    this.previewScreen(screen);
  };

  /**
   * @param {string} id - Screen id to preview.
   */
  Quiz.prototype.previewScreen = function (id) {
    var sample = {};

    this.questions.forEach(function (question) {
      var preferred = PREVIEW_SAMPLE[question.id];
      var match = question.options.filter(function (option) {
        return option.value === preferred;
      })[0] || question.options[0];

      if (match) {
        sample[question.id] = match.value;
      }
    });

    this.setAnswers(sample);

    if (id === 'results') {
      this.buildResults();
    }

    this.show(id, false, false);
  };

  Quiz.prototype.destroy = function () {
    this.clearAnalysis();
    clearTimeout(this.scrollTimer);

    if (this.observer) {
      this.observer.disconnect();
    }
  };

  /**
   * Boots (or re-boots) the quiz for the root on the page.
   */
  function init() {
    var root = document.querySelector('[data-w2s-quiz]');

    if (instance) {
      instance.destroy();
      instance = null;
    }

    if (!root) {
      return;
    }

    instance = new Quiz(root);
  }

  /* Global listeners (installed once, delegate to the current instance). */

  document.addEventListener('keydown', function (event) {
    if (!instance) {
      return;
    }

    if (event.key !== 'Enter' || !instance.getQuestion(instance.current)) {
      return;
    }

    if (event.target.closest && event.target.closest('.choice')) {
      return;
    }

    if (event.target.tagName === 'BUTTON') {
      return;
    }

    event.preventDefault();
    instance.next(instance.current);
  });

  window.addEventListener('popstate', function (event) {
    var saved = event.state && event.state.w2sQuiz;

    if (!instance || !saved) {
      return;
    }

    instance.applySnapshot(saved);
  });

  function reschedule() {
    if (instance) {
      instance.scheduleConditionalScroll();
    }
  }

  addEventListener('resize', reschedule, { passive: true });
  addEventListener('orientationchange', reschedule, { passive: true });
  addEventListener('load', reschedule, { once: true });
  document.addEventListener('click', function () {
    setTimeout(reschedule, 0);
  }, true);

  window.__syncMobileConditionalScroll = reschedule;

  /* Theme editor hooks. */

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;

    if (target.querySelector('[data-w2s-quiz]') || target.querySelector('[data-w2s-quiz-answers]')) {
      init();
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    if (!instance || !instance.root.contains(event.target)) {
      return;
    }

    var screen = event.target.closest('[data-screen]');

    if (screen && screen.getAttribute('data-screen') !== 'results') {
      instance.previewScreen(screen.getAttribute('data-screen'));
      return;
    }

    var tab = event.target.closest('[data-damage-tab]');

    if (tab && instance.selectTab) {
      instance.previewScreen('results');
      instance.selectTab(tab.getAttribute('data-damage-tab'), false);
      return;
    }

    var results = instance.root.querySelector('#results');

    if (results && results.contains(event.target)) {
      instance.previewScreen('results');
    }
  });

  /* Test hooks (kept from the export). */

  window.__quizTest = {
    get questions() {
      return instance ? instance.sequence() : [];
    },
    paths: {
      handheld: { shower: 'handheld' },
      rainfall: { shower: 'rainfall' },
      bundle: { shower: 'both' },
      uncertain: { shower: 'unsure' },
    },
    setAnswers: function (obj) {
      return instance ? instance.setAnswers(obj) : null;
    },
    getAnswers: function () {
      return instance ? JSON.parse(JSON.stringify(instance.answers)) : {};
    },
    getState: function () {
      return instance ? instance.getState() : null;
    },
    resolve: function (obj) {
      if (!instance) {
        return null;
      }

      return obj ? instance.setAnswers(obj) : instance.resolve();
    },
    breakdown: function (obj) {
      if (!instance) {
        return [];
      }

      if (obj) {
        instance.setAnswers(obj);
      }

      return instance.getBreakdownCards();
    },
    reports: function (obj) {
      if (!instance) {
        return [];
      }

      if (obj) {
        instance.setAnswers(obj);
      }

      return instance.getCustomerReports();
    },
    show: function (id) {
      if (!instance) {
        return null;
      }

      if (id === 'results') {
        instance.buildResults();
      }

      instance.show(id, false, false);

      return instance.getState();
    },
    reset: function () {
      if (instance) {
        instance.reset();
      }
    },
  };

  if (!window.__W2S_QUIZ__) {
    Object.defineProperty(window, '__W2S_QUIZ__', {
      value: Object.freeze({
        getState: function () {
          return window.__quizTest.getState();
        },
        reset: function () {
          return window.__quizTest.reset();
        },
      }),
      writable: false,
      configurable: false,
      enumerable: true,
    });
  }

  init();
})();
